import { createSelector } from '@reduxjs/toolkit';
import type { RootStateWithInjection } from '@restore/store';
import {
  selectReaderTaskState,
  selectReadingDicomStudies,
} from 'src/store-redux/readerTask/readerTask.slice';
import { LabelMerged } from '@shared/types/imagingLabels';
import { Context } from '@shared/types/clinicalTrialContext';
import { ViewerVisit } from '@shared/types/visits';
import {
  calcReaderTaskCompleted,
  getAllVisitLabels,
  makeAllConfigLabelsFlat,
} from '../../transformers';
import type {
  ChildTask,
  ReaderTaskDicomStudy,
  ReaderTaskType,
  ReaderTaskVisits,
} from '@shared/types/reader';
import { StudyType } from '@shared/services/communication';
import { selectReaderSubTaskState } from 'src/store-redux/readerSubTask/readerSubTask.slice';
import { readerTaskOrchestrationSlice } from 'src/store-redux/readerTaskOrchestration/readerTaskOrchestration.slice';
import { OHIFViewerModeType } from '@shared/types/viewerMode';

/** One visit as it leaves either source, before labels are resolved onto it. */
type JoinedVisit = Partial<ReaderTaskDicomStudy> & {
  scoringCode?: string;
  visitName?: string;
  visitOrder?: number;
  /** Set only by the grouped source — each visit belongs to its own child task. */
  taskId?: string;
};

export type ReadingVisitSources = {
  /** Every study of the reading, as the PARENT task reports them. */
  readingStudies?: ReaderTaskDicomStudy[];
  /** The parent's child tasks — where a visit's name and order come from. */
  childTasks?: ChildTask[];
  /** The sub-task actually loaded, i.e. the visit being scored. */
  currentTask?: Partial<ReaderTaskType> | null;
};

/**
 * Build one visit per study of the whole reading
 *
 * Plain terms
 * -----------
 * A reading that spans several visits keeps its images spread across them. This
 * puts all of them side by side, in the order the visits happened, so the reader
 * can compare a follow-up against the baseline without leaving the form.
 *
 * Technical
 * ---------
 * The studies come from the parent-addressed `dicom/series` endpoint and arrive
 * in no useful order (A2); name and order are joined from `childTasks` by
 * `visitId`, which is the only trustworthy source for `visitOrder` (A3). A study
 * whose visit matches no child task is KEPT with an undefined order and sorts
 * last — dropping it would hide images that exist. So is a study with no series
 * yet: `makeStudyAndSeriesUids` skips it when the viewer builds
 * `StudyInstanceUIDs` (C7), so it costs nothing, and it is what keeps a
 * disabled-visit state implementable later without another round trip.
 *
 * `scoringCode` is taken from the loaded sub-task and therefore lands on the
 * visit being scored alone — `study.saga.ts` reads `scoringFormCode` off the
 * current visit, and a prior visit's form code is not ours to guess.
 */
const makeVisitsFromReadingStudies = ({
  readingStudies = [],
  childTasks = [],
  currentTask,
}: ReadingVisitSources): JoinedVisit[] => {
  /**
   * A reader sees the visits of the reading that have been opened
   *
   * Plain terms
   * -----------
   * A longitudinal reading is worked one visit at a time, and a visit still
   * waiting its turn is locked in the scoring form. Offering its images in the
   * study list contradicts the form sitting beside it and invites scoring out of
   * turn. A visit already worked stays on the list from anywhere in the reading
   * — stepping back to an earlier visit is how a reader compares against it, and
   * the later ones must not vanish while they do.
   *
   * Technical
   * ---------
   * The test is the child task's status, not the visit's position. A positional
   * cut at the visit being scored was the first attempt and was wrong in exactly
   * one place: from an earlier visit it hid the later ones the reader had
   * already completed.
   *
   * `CREATED` and `READING_PENDING` are the two states that mean "not opened
   * yet". `TERMINATED` and `FAILED` are kept: that reading happened, however it
   * ended. The visit being scored is always kept, whatever its status says — the
   * orchestrator opens a reading on a `READING_PENDING` child and starts it
   * afterwards, so on first open the current visit is pending too. A study whose
   * visit matches no child task is kept as well: nothing is known against it,
   * and an empty list means an empty viewer (D7-7712, 2.7).
   */
  const NOT_YET_OPEN: ChildTask['status'][] = ['CREATED', 'READING_PENDING'];

  const childTaskByVisitId = new Map(
    childTasks.map(childTask => [childTask.visitId, childTask])
  );

  const isOpenToTheReader = (study: ReaderTaskDicomStudy) => {
    if (study.visitId === currentTask?.visitId) {
      return true;
    }

    const childTask = childTaskByVisitId.get(study.visitId);

    return !childTask || !NOT_YET_OPEN.includes(childTask.status);
  };

  return readingStudies
    .filter(isOpenToTheReader)
    .map(study => {
      const childTask = childTaskByVisitId.get(study.visitId);

      const currentVisit = currentTask?.visits?.find(
        v => v.visitId === study.visitId
      );

      return {
        ...study,
        scoringCode: currentVisit?.scoringCode,
        visitName: childTask?.visitName,
        visitOrder: childTask?.visitOrder,
        /**
         * The visit being scored is not special-cased here
         *
         * Plain terms
         * -----------
         * Every visit is labelled with the task that owns it, including the one
         * the reader is working on — the parent lists that task like any other.
         *
         * Technical
         * ---------
         * The design's pseudocode reached for the loaded sub-task's own `id` for
         * the current visit. It is the same id: the parent's `childTasks` entry
         * for that `visitId` names that very task. And if the parent ever
         * answered without it, `childTask?.id` is undefined and the second map's
         * `?? taskId` falls back to the loaded sub-task — which is the id the
         * design wanted — so the simpler form degrades to the defensive one
         * rather than away from it (D7-7712, C2).
         */
        taskId: childTask?.id,
      };
    })
    .sort((a, b) => {
      if (a.visitOrder === b.visitOrder) {
        return 0;
      }

      // An unmatched study has no place in the timeline, so it goes to the end
      // rather than to the front, where `undefined` would otherwise put it.
      if (a.visitOrder === undefined) {
        return 1;
      }
      if (b.visitOrder === undefined) {
        return -1;
      }

      return a.visitOrder - b.visitOrder;
    });
};

/**
 * Returns every visit the viewer should show, with its labels resolved
 *
 * @param allConfigLabels - all DICOM labels from configuration, flat (merged with modalityName)
 * @param config - clinical trial context
 * @param taskId - the loaded task, used when a visit names no task of its own
 * @param taskVisits - the loaded task's own visits (the single-visit source)
 * @param sources - the grouped source: the reading's studies plus its child tasks
 * @returns {ViewerVisit[]} - all visits with labels
 */
export const makeJoinedVisitsForReader = (
  allConfigLabels: LabelMerged[],
  config: Context | undefined,
  taskId: string,
  taskVisits: ReaderTaskVisits[],
  sources?: ReadingVisitSources
): ViewerVisit[] => {
  const configVisits = config?.cohorts[0].visits ?? [];

  const visits: JoinedVisit[] | undefined = sources?.readingStudies?.length
    ? makeVisitsFromReadingStudies(sources)
    : taskVisits?.map(taskVisit => ({
        ...taskVisit.dicomStudy,
        scoringCode: taskVisit.scoringCode,
        visitName: taskVisit.visitName,
        visitOrder: taskVisit.visitOrder,
      }));

  return (visits ?? []).map(visit => {
    const {
      dicomSeries,
      visitId,
      id,
      visitName,
      studyInstanceUid,
      visitOrder,
      scoringCode,
      taskId: visitTaskId,
    } = visit;

    const configVisit = configVisits.find(cv => cv.id === visitId);

    const allLabels = getAllVisitLabels(
      allConfigLabels,
      configVisit?.labels ?? []
    );

    return {
      dicomSeries,
      visitId,
      studyInstanceUid,
      visitOrder,
      id,
      /**
       * Each visit answers for its OWN task
       *
       * Plain terms
       * -----------
       * The viewer uses this to tell which visit a series belongs to. With one
       * visit per reading the distinction never mattered; with several, stamping
       * them all with the visit being scored makes every earlier visit look like
       * the current one.
       *
       * Technical
       * ---------
       * The grouped source sets a per-visit id from `childTasks`; the parameter
       * is the fallback, which is what the single-visit path has always used, so
       * that path stays byte-identical (C2).
       */
      taskId: visitTaskId ?? taskId,
      allLabels,
      type: 'REGULAR',
      visitName,
      scoringCode,
    } as ViewerVisit;
  });
};

export const isViewerAllowedToLoadForReader = createSelector(
  [selectReaderTaskState, selectReaderSubTaskState],
  (readerTask, subTaskReaderTask) => {
    const mainTaskVisits = readerTask.data?.visits ?? [];
    const subTaskVisits = Object.values(subTaskReaderTask.data ?? {}).flatMap(
      subTask => subTask.visits ?? []
    );

    const allVisits = [...mainTaskVisits, ...subTaskVisits];

    const hasSeries = allVisits.some(
      visit => !!visit?.dicomStudy?.dicomSeries?.length
    );

    /**
     * A grouped reading can have images the tasks know nothing about
     *
     * Plain terms
     * -----------
     * For a reading that spans several visits, the record of the visit being
     * scored does not always carry its own images — but the reading as a whole
     * does. Judging by the task alone would refuse to open the viewer and show
     * "No data to display" over a reading that is full of images.
     *
     * Technical
     * ---------
     * A parent's own `visits[]` is empty and a child task's `dicomStudy` can be
     * null (A6), so the `dicom/series` answer is the only evidence there is. It
     * is consulted in addition to, never instead of, the task visits — the
     * non-grouped path must keep refusing exactly when it refuses today (3.9).
     *
     * There is no identity check here on purpose. The list is cleared by
     * `fetchReaderNeededDataWorker` before the viewer opens on any reading it
     * was not fetched for (C17), so by the time this runs it is either this
     * reading's answer or empty. Re-testing it against `readerTask.data.id`
     * would be worse than nothing: `lazyPrefetchSubtasksWorker` can leave a
     * CHILD in that slice, and the studies would then be discarded for the very
     * grouped reading they belong to.
     */
    const hasReadingSeries = (readerTask.readingDicomStudies ?? []).some(
      study => !!study?.dicomSeries?.length
    );

    return hasSeries || hasReadingSeries;
  }
);

export const prepareReaderStudy = (
  s: RootStateWithInjection<[typeof readerTaskOrchestrationSlice]>
): StudyType => {
  const {
    auth,
    clinicalTrialContext,
    readerTask,
    readerSubTask,
    readerTaskOrchestration,
    clinicalTrialInventory,
  } = s;

  const subTaskId = readerTaskOrchestration.subTaskId;

  const subTask = subTaskId ? readerSubTask.data[subTaskId] : null;

  const task = subTask ?? readerTask.data;

  if (subTaskId && !subTask) {
    alert('subTask not found');
  }

  const allConfigLabels = makeAllConfigLabelsFlat(
    clinicalTrialContext.data?.configuration.labelGroups ?? []
  );

  /**
   * The grouped path is entered on IDENTITY, not on the presence of a sub-task
   *
   * Plain terms
   * -----------
   * To list every visit of a reading we need the reading itself — the task that
   * owns the others. What sits in the store is not always that task, and joining
   * the visits against the wrong one would produce a list of unnamed visits in
   * an arbitrary order. When we cannot be sure, we show the one visit we know
   * is right, which is what the app did before this ticket.
   *
   * Technical
   * ---------
   * `readerTask.data` is written by whichever fetch landed last, and
   * `lazyPrefetchSubtasksWorker` overwrites it with a prefetched sibling after
   * the first visit switch (C3). Checking `childTasks.length` would not tell
   * child from parent either — a child has none, but so could a malformed
   * parent. The orchestrator's `taskId` is the reading the page was opened on,
   * so comparing against it is the only check that cannot drift.
   *
   * This guard is a safety net, not the plan: the saga fetches the parent as a
   * precondition, so falling through here means the reading opens with one
   * visit — the bug. It earns its place against a malformed response, a role
   * whose parent fetch is refused, or a prefetch landing mid-switch.
   */
  const parentTask =
    subTaskId && readerTask.data?.id === readerTaskOrchestration.taskId
      ? readerTask.data
      : null;

  const visits = makeJoinedVisitsForReader(
    allConfigLabels,
    clinicalTrialContext.data?.configuration,
    task?.id ?? '',
    task?.visits ?? [],
    {
      readingStudies: parentTask ? selectReadingDicomStudies(s) : [],
      childTasks: parentTask?.childTasks,
      currentTask: subTask,
    }
  );

  return {
    clinicalTrialId: clinicalTrialContext.data?.configuration.id ?? '',
    clinicalTrialCode: clinicalTrialContext.data?.configuration.code ?? '',
    clinicalTrialName: clinicalTrialContext.data?.configuration.name ?? '',
    currentUserUid: auth.data?.['dynamika7/user_metadata'].uuid ?? '',
    DICOMResource: readerTask.DICOMResource ?? '',
    readerParentTask: subTaskId ? readerTask.data : null,
    task: {
      assigneeId: task?.assigneeId ?? '',
      id: task?.id ?? '',
      patientCode: task?.patientCode ?? '',
      patientDateOfBirth: '',
      completed: calcReaderTaskCompleted(task?.status),
    },
    visitId: task?.visitId,
    instruments: clinicalTrialInventory.instruments,
    tokenUrl: '',
    viewerMode: OHIFViewerModeType.READER,
    visits,
    clinicalTrialInfo: {
      readingName: clinicalTrialContext.data?.configuration?.name ?? '',
      readingCode: readerTask.data?.readingCode ?? '',
    },
  };
};
